import React, { useCallback } from 'react';
import { useCardBackBannerQuery } from './CardBackBannerQuery.generated';
import { ArchivedCardBanner } from './ArchivedCardBanner';
import { ArchivedListBanner } from './ArchivedListBanner';
import { TemplateCardBanner } from './TemplateCardBanner';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary/ErrorBoundary';
import { Null } from 'app/src/components/Null';
import { getFreeTrialProperties } from '@trello/organizations';
import { useOrgFreeTrialQuery } from './OrgFreeTrialQuery.generated';
import { Auth } from 'app/scripts/db/auth';
import { ViewSuggestionByPriority } from './ViewSuggestionByPriority';
interface CardBackBannerProps {
  idCard: string;
  editable: boolean;
  idOrg?: string;
  setIdToDismiss: (id: string) => void;
  isBC?: boolean;
}

export const CardBackBanner: React.FunctionComponent<CardBackBannerProps> = ({
  idCard,
  editable,
  idOrg,
  setIdToDismiss,
  isBC,
}) => {
  const { data, loading, error } = useCardBackBannerQuery({
    variables: {
      idCard,
    },
  });

  const { data: orgCreditData } = useOrgFreeTrialQuery(
    idOrg && isBC
      ? {
          variables: {
            orgId: idOrg,
          },
        }
      : { skip: true },
  );

  const setIdToDismissCallback = useCallback((id) => setIdToDismiss(id), [
    setIdToDismiss,
  ]);

  const allSuggestedBannersDismissed =
    Auth.me().isDismissed(`timeline-view-suggestion-${idOrg}`) &&
    Auth.me().isDismissed(`calendar-view-suggestion-${idOrg}`);

  const card = data && data.card ? data.card : null;
  const orgCredits = orgCreditData?.organization?.credits;
  const orgProducts = orgCreditData?.organization?.products;
  const orgPaidAccountTrialExpiration =
    orgCreditData?.organization?.paidAccount?.trialExpiration;

  const hasActiveFreeTrial = useCallback(() => {
    const freeTrialProperties =
      orgCredits && orgProducts
        ? getFreeTrialProperties(
            orgCredits,
            orgProducts,
            orgPaidAccountTrialExpiration || '',
          )
        : undefined;

    return freeTrialProperties?.isActive;
  }, [orgCredits, orgPaidAccountTrialExpiration, orgProducts]);

  if (!card || loading || error) {
    return null;
  }

  const hasCover = !!(
    card.cover &&
    (card.cover.color ||
      card.cover.idAttachment ||
      card.cover.idUploadedBackground)
  );

  const hasStickers = card.stickers && card.stickers.length > 0;

  if (card.isTemplate) {
    return (
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-panorama',
          feature: 'Template Card',
        }}
        errorHandlerComponent={Null}
      >
        <TemplateCardBanner
          idCard={idCard}
          hasStickers={hasStickers}
          hasCover={hasCover}
          editable={editable}
        />
      </ErrorBoundary>
    );
  }

  if (card.closed) {
    return <ArchivedCardBanner />;
  }

  if (card.list.closed) {
    return <ArchivedListBanner />;
  }

  if (idOrg && hasActiveFreeTrial() && !allSuggestedBannersDismissed) {
    return (
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-panorama',
          feature: 'View Suggestion Upsell Banner',
        }}
      >
        <ViewSuggestionByPriority
          idCard={idCard}
          idBoard={card.idBoard}
          idOrg={idOrg}
          hasStickers={hasStickers}
          hasCover={hasCover}
          setIdToDismiss={setIdToDismissCallback}
        />
      </ErrorBoundary>
    );
  }

  return null;
};
